import React, {useEffect, useState} from 'react';
import {UserCog, Mail, User, Save} from 'lucide-react';
import ClientSelectSearch from '../ClientSelectSearch/ClientSelectSearch';
import useApi from "../../../utils/useApi";
import Swal from 'sweetalert2';

const UserManagement = ({ handleShowHeader, handleShowSettingsHeader }) => {
    const { updateUserType, searchClients, updateProfile } = useApi();
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [editForm, setEditForm] = useState({
                                                 client: '',
                                                 email: '',
                                                 client_id: ''
                                             });

    useEffect(() => {
        handleShowHeader(true);
        handleShowSettingsHeader(false);
        initialize();
    }, []);

    useEffect(() => {
        if (currentUser) {
            setEditForm({
                            client: currentUser.client,
                            email: currentUser.email,
                            client_id: currentUser.client_id
                        });
        }
    }, [currentUser]);

    const initialize = async () => {
        const response = await searchClients({ term: '' });
        setClients(response.clients.filter(user => user.user_type === 'client'));
        setAdmins(response.admins || response.clients.filter(user => user.user_type === 'admin'));
    };

    const handleUserSelect = (user) => {
        setCurrentUser(user);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdateUserDetails = async (e) => {
        e.preventDefault();
        if (!currentUser) return;

        try {
            setLoading(true);
            await updateProfile(currentUser.id, editForm);

            // Update local state
            const updatedUser = { ...currentUser, ...editForm };
            if (currentUser.user_type === 'admin') {
                setAdmins(prev => prev.map(admin =>
                                               admin.id === currentUser.id ? updatedUser : admin
                ));
            } else {
                setClients(prev => prev.map(client =>
                                                client.id === currentUser.id ? updatedUser : client
                ));
            }
            setCurrentUser(updatedUser);

            await Swal.fire({
                                title: 'Success!',
                                text: 'User details updated successfully',
                                icon: 'success',
                                confirmButtonColor: '#3085d6'
                            });
        }
        catch (err) {
            await Swal.fire({
                                title: 'Error',
                                text: err.message || 'Failed to update user details',
                                icon: 'error',
                                confirmButtonColor: '#dc3545'
                            });
        }
        finally {
            setLoading(false);
        }
    };

    const handleUserTypeChange = async (newType) => {
        if (!currentUser) return;

        try {
            setLoading(true);
            await updateUserType(currentUser.id, newType);

            const updatedUser = { ...currentUser, user_type: newType };

            if (newType === 'admin') {
                setClients(prev => prev.filter(c => c.id !== currentUser.id));
                setAdmins(prev => [...prev, updatedUser]);
            } else {
                setAdmins(prev => prev.filter(a => a.id !== currentUser.id));
                setClients(prev => [...prev, updatedUser]);
            }

            setCurrentUser(updatedUser);

            await Swal.fire({
                                title: 'Success!',
                                text: 'User type updated successfully',
                                icon: 'success',
                                confirmButtonColor: '#3085d6'
                            });
        }
        catch (err) {
            await Swal.fire({
                                title: 'Error',
                                text: err.message || 'Failed to update user type',
                                icon: 'error',
                                confirmButtonColor: '#dc3545'
                            });
        }
        finally {
            setLoading(false);
        }
    };

    const AdminCard = ({ admin }) => (
        <div
            onClick={() => handleUserSelect(admin)}
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 cursor-pointer
                transition-all duration-200 hover:shadow-lg
                ${currentUser?.id === admin.id ? 'ring-2 ring-blue-500' : ''}
                border border-gray-200 dark:border-gray-700`}
        >
            <div className="mb-2">
                <div className="flex items-center gap-2 mb-1">
                    <User className="h-5 w-5 text-gray-600 dark:text-gray-400"/>
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">
                        {admin.client}
                    </h3>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Mail className="h-4 w-4"/>
                    <span className="truncate">{admin.email}</span>
                </div>
            </div>
        </div>
    );

    const AdminCards = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {admins.map((admin) => (
                <AdminCard key={admin.id} admin={admin}/>
            ))}
        </div>
    );

    return (
        <div className="p-6 max-w-6xl mx-auto page-container z-10 relative">
            <div className="flex items-center gap-2 mb-6">
                <UserCog className="h-6 w-6 text-gray-600 dark:text-gray-400"/>
                <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
                    User Management
                </h1>
            </div>

            <div className="space-y-6">
                <div>
                    <h2 className="text-xl font-medium text-gray-800 dark:text-gray-200 mb-4">
                        Administrators
                    </h2>
                    <AdminCards/>
                </div>

                <div>
                    <h2 className="text-xl font-medium text-gray-800 dark:text-gray-200 mb-4">
                        Select Client User
                    </h2>
                    <ClientSelectSearch
                        clients={clients}
                        includeLink={false}
                        currentClient={currentUser}
                        onSelectClient={handleUserSelect}
                    />
                </div>

                {currentUser && (
                    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                            User Management for {currentUser.client}
                        </h2>

                        {/* User Details Form */}
                        <form onSubmit={handleUpdateUserDetails} className="mb-6 space-y-4">
                            <div>
                                <label htmlFor="client" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Name
                                </label>
                                <input
                                    type="text"
                                    id="client"
                                    name="client"
                                    value={editForm.client}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                                        bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                                        focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                        disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={loading}
                                />
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={editForm.email}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                                        bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                                        focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                        disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={loading}
                                />
                            </div>
                            <div>
                                <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Client ID
                                </label>
                                <input
                                    type="text"
                                    id="client_id"
                                    name="client_id"
                                    value={editForm.client_id}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                                        bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                                        focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                        disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={loading}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md
                                    hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500
                                    disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Save className="h-4 w-4"/>
                                Save Changes
                            </button>
                        </form>

                        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                            <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">
                                User Type
                            </h3>
                            <div className="space-y-3">
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={() => handleUserTypeChange('client')}
                                        disabled={loading || currentUser.user_type === 'client'}
                                        className={`px-4 py-2 rounded-md transition-colors duration-200
                                            ${currentUser.user_type === 'client'
                                              ? 'bg-blue-600 dark:bg-blue-500 text-white'
                                              : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                        Client
                                    </button>
                                    <button
                                        onClick={() => handleUserTypeChange('admin')}
                                        disabled={loading || currentUser.user_type === 'admin'}
                                        className={`px-4 py-2 rounded-md transition-colors duration-200
                                            ${currentUser.user_type === 'admin'
                                              ? 'bg-blue-600 dark:bg-blue-500 text-white'
                                              : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                        Admin
                                    </button>
                                </div>

                                {loading && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        Updating user...
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserManagement;
